import React, { useState, useEffect, useCallback } from 'react';
import { ref, onValue, get, update, set } from 'firebase/database';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { database, auth, businessPlansAPI, goalsAPI } from '../firebase';
import './MyAIPage.css';

// Import other components
import GlassEditor from './GlassEditor';
import ScientificEditorSystem from './EditorToggleSystem';
import GoalSidebar from './GlassEditor/GoalSidebar';
import GoalOnboarding from './GoalOnboarding';
import VisionBoard from './VisionBoard';
import VisionPage from './VisionPage';
import JourneyRibbon from './Journey/JourneyRibbon';
import ErrorBoundary from './ErrorBoundary';

function MyAIPage() {
  const [user, setUser] = useState(null);
  const [projects, setProjects] = useState([]);
  const [sharedProjects, setSharedProjects] = useState([]);
  const [hasEntryToday, setHasEntryToday] = useState(false);
  const [showCalendar, setShowCalendar] = useState(true);
  const [activeView, setActiveView] = useState('glass'); // 'glass' | 'scientific'
  const [businessPlans, setBusinessPlans] = useState([]);
  const [userGoals, setUserGoals] = useState([]);
  const [plansLoaded, setPlansLoaded] = useState(false);
  const [journey, setJourney] = useState(null);
  const [journeyLoaded, setJourneyLoaded] = useState(false);
  const [activeSelection, setActiveSelection] = useState(null); // {type:'plan'|'project', id, title}
  const [onboarding, setOnboarding] = useState(null); // null | {existingGoal?, initialStep?}
  const [resumeDismissed, setResumeDismissed] = useState(false);
  const [visionMode, setVisionMode] = useState(null); // null|'board'|'page'
  const [sidebarExpanded, setSidebarExpanded] = useState(true);
  const [externalEntry, setExternalEntry] = useState(null);

  // Authentication listener
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (currentUser) => {
      setUser(currentUser);
    });
    return () => unsubscribe();
  }, []);

  // Load user projects
  useEffect(() => {
    if (!user) {
      setProjects([]);
      setSharedProjects([]);
      return;
    }

    // Load user's own projects
    const projectsRef = ref(database, `users/${user.uid}/projects`);
    const projectsSubscription = onValue(projectsRef, async (snapshot) => {
      const projectsData = snapshot.val() || {};

      const projectsArray = await Promise.all(
        Object.keys(projectsData).map(async (projectId) => {
          const projectRef = ref(database, `projects/${projectId}`);
          const projectSnap = await get(projectRef);
          const projectDetails = projectSnap.val() || {};

          return {
            id: projectId,
            title: projectDetails.title || 'Untitled Project',
            createdAt: projectDetails.createdAt,
            isOwner: true,
            hasData: true // Changed as we're using a different data structure
          };
        })
      );

      setProjects(projectsArray);
    });

    // Load shared projects
    const sharedProjectsRef = ref(database, `users/${user.uid}/shared_projects`);
    const sharedProjectsSubscription = onValue(sharedProjectsRef, async (snapshot) => {
      const sharedData = snapshot.val() || {};

      const sharedArray = await Promise.all(
        Object.keys(sharedData).map(async (projectId) => {
          const projectRef = ref(database, `projects/${projectId}`);
          const projectSnap = await get(projectRef);
          const projectDetails = projectSnap.val() || {};

          return {
            id: projectId,
            title: projectDetails.title || 'Untitled Project',
            createdAt: projectDetails.createdAt,
            accessGranted: sharedData[projectId].accessGranted,
            isOwner: false,
            hasData: true // Changed as we're using a different data structure
          };
        })
      );

      setSharedProjects(sharedArray);
    });

    return () => {
      projectsSubscription();
      sharedProjectsSubscription();
    };
  }, [user]);

  // Load business plans and goals for the plan switcher
  const loadPlansAndGoals = useCallback(async () => {
    if (!user) {
      setBusinessPlans([]);
      setUserGoals([]);
      return;
    }
    try {
      const [plansResult, goalsResult] = await Promise.all([
        businessPlansAPI.getUserBusinessPlans(user.uid),
        goalsAPI.getUserGoals(user.uid)
      ]);
      if (plansResult?.success) setBusinessPlans(plansResult.plans || []);
      if (goalsResult?.success) setUserGoals(goalsResult.goals || []);
    } finally {
      setPlansLoaded(true);
    }
  }, [user]);

  useEffect(() => {
    loadPlansAndGoals();
  }, [loadPlansAndGoals]);

  // Load persisted journey state (selection + onboarding dismissal) for this user
  useEffect(() => {
    if (!user) {
      setJourney(null);
      setJourneyLoaded(false);
      return;
    }
    get(ref(database, `users/${user.uid}/journey`))
      .then((snap) => setJourney(snap.val() || {}))
      .catch(() => setJourney({}))
      .finally(() => setJourneyLoaded(true));
  }, [user]);

  // Auto-select the first available plan/project once data loads.
  // Prefer the last selection persisted to users/{uid}/journey, then fall back.
  useEffect(() => {
    if (activeSelection) return;
    if (!journeyLoaded) return;
    const saved = journey?.activePlanId && (
      (journey.activeType === 'plan'
        && businessPlans.find(p => p.id === journey.activePlanId)
        && { type: 'plan', id: journey.activePlanId, title: businessPlans.find(p => p.id === journey.activePlanId).title })
      || (journey.activeType === 'project'
        && [...projects, ...sharedProjects].find(p => p.id === journey.activePlanId)
        && { type: 'project', id: journey.activePlanId, title: [...projects, ...sharedProjects].find(p => p.id === journey.activePlanId).title })
    );
    if (saved) {
      setActiveSelection(saved);
      return;
    }
    if (businessPlans.length > 0) {
      setActiveSelection({ type: 'plan', id: businessPlans[0].id, title: businessPlans[0].title });
    } else if (projects.length > 0) {
      setActiveSelection({ type: 'project', id: projects[0].id, title: projects[0].title });
    } else if (sharedProjects.length > 0) {
      setActiveSelection({ type: 'project', id: sharedProjects[0].id, title: sharedProjects[0].title });
    }
  }, [activeSelection, businessPlans, projects, sharedProjects, journeyLoaded, journey]);

  // Persist the active selection to users/{uid}/journey so it restores next visit
  useEffect(() => {
    if (user && journeyLoaded && activeSelection) {
      update(ref(database, `users/${user.uid}/journey`), {
        activePlanId: activeSelection.id,
        activeType: activeSelection.type,
        lastVisitAt: Date.now()
      }).catch(() => {});
    }
  }, [activeSelection, user, journeyLoaded]);

  // Zero-state: a brand-new user with nothing yet (and no prior dismissal) lands in onboarding
  useEffect(() => {
    if (plansLoaded && journeyLoaded && !onboarding
      && businessPlans.length === 0 && projects.length === 0
      && sharedProjects.length === 0 && userGoals.length === 0
      && !journey?.onboardingDismissed) {
      setOnboarding({});
    }
  }, [plansLoaded, journeyLoaded, businessPlans, projects, sharedProjects, userGoals, journey, onboarding]);

  // Whether the user has journaled today — drives the Journey ribbon "Practice" step.
  // Read-only; MyAIPage owns this listener (GoalSidebar keeps its own separate one).
  useEffect(() => {
    if (!user) {
      setHasEntryToday(false);
      return;
    }
    const entriesRef = ref(database, `structuredEntries/${user.uid}`);
    const unsubscribe = onValue(entriesRef, (snapshot) => {
      const all = snapshot.val() || {};
      const todayStr = new Date().toDateString();
      const has = Object.values(all).some((entry) => {
        const ts = entry?.createdAt || entry?.date;
        if (!ts) return false;
        return new Date(ts).toDateString() === todayStr;
      });
      setHasEntryToday(has);
    });
    return unsubscribe;
  }, [user]);

  // Handle sign out
  const handleSignOut = () => {
    signOut(auth).catch(error => {
      console.error("Sign out error:", error);
    });
  };

  // Derived active plan/goal for the selected tab
  const activePlan = activeSelection?.type === 'plan'
    ? businessPlans.find(p => p.id === activeSelection.id) || null
    : null;
  const activeGoal = activePlan
    ? (userGoals.find(g => g.id === activePlan.goalId) || null)
    : activeSelection?.type === 'project'
      ? { id: activeSelection.id, title: activeSelection.title, goal: activeSelection.title, description: `Project: ${activeSelection.title}` }
      : null;

  // A goal the user started but never turned into a plan → offer to resume
  const goalNeedingPlan = plansLoaded
    ? userGoals.find(g => g.status !== 'archived' && !businessPlans.some(p => p.goalId === g.id))
    : null;

  // Render main content
  const renderMainContent = () => {
    if (!user) {
      return null;
    }

    if (!plansLoaded || !journeyLoaded) {
      return (
        <div className="loading-container myai-loading">
          <div className="loading-spinner" />
          <p>Loading your journey…</p>
        </div>
      );
    }

    if (onboarding) {
      return (
        <GoalOnboarding
          user={user}
          existingGoal={onboarding.existingGoal}
          initialStep={onboarding.initialStep}
          onComplete={async (goalData, conversationData, businessPlanData) => {
            setOnboarding(null);
            await loadPlansAndGoals();
            if (businessPlanData?.id) {
              setActiveSelection({
                type: 'plan',
                id: businessPlanData.id,
                title: businessPlanData.title || goalData?.goal || 'Business Plan'
              });
            }
          }}
          onBack={() => setOnboarding(null)}
          onSkip={() => {
            setJourney(p => ({ ...(p || {}), onboardingDismissed: true }));
            setOnboarding(null);
          }}
        />
      );
    }

    if (visionMode === 'board') {
      return (
        <div className="myai-vision-wrapper">
          <div className="myai-vision-controls">
            <button onClick={() => setVisionMode(null)}>← Back to Journal</button>
            <button onClick={() => setVisionMode('page')}>View Vision →</button>
          </div>
          <VisionBoard user={user} userGoal={activeGoal} />
        </div>
      );
    }

    if (visionMode === 'page') {
      return (
        <VisionPage
          user={user}
          userGoal={activeGoal}
          onNext={() => setVisionMode(null)}
          onBack={() => setVisionMode('board')}
        />
      );
    }

    if (activeView === 'scientific') {
      return (
        <ScientificEditorSystem
          onSave={async (text) => {
            try {
              await set(ref(database, `users/${user.uid}/scientificDocs/latest`), { content: text, updatedAt: Date.now() });
            } catch (e) {
              console.error('Failed to save scientific doc:', e);
            }
          }}
        />
      );
    }

    return (
      <>
        {goalNeedingPlan && !resumeDismissed && (
          <div className="myai-resume-banner" role="status">
            <span>Continue building your plan for {goalNeedingPlan.title}</span>
            <button onClick={() => setOnboarding({ existingGoal: goalNeedingPlan, initialStep: 'manifestation' })}>Continue</button>
            <button aria-label="Dismiss" onClick={() => setResumeDismissed(true)}>×</button>
          </div>
        )}
        <GlassEditor
          showCalendar={showCalendar}
          userGoal={activeGoal}
          businessPlan={activePlan}
          leftSidebarExpanded={sidebarExpanded}
          externalEntry={externalEntry}
          leftSidebar={
            <GoalSidebar
              user={user}
              userGoal={activeGoal}
              businessPlan={activePlan}
              expanded={sidebarExpanded}
              onToggle={() => setSidebarExpanded(e => !e)}
              onOpenEntry={(entry) => setExternalEntry({ entry, ts: Date.now() })}
              onOpenVisionBoard={() => setVisionMode('board')}
              onCreatePlan={() => setOnboarding({})}
            />
          }
        />
      </>
    );
  };

  return (
    <div className="myai-page">
      <ErrorBoundary label="My AI">
      {user && (
        <div className="myai-header">
          <div className="myai-header-left">
            <h1>{activeSelection?.title || 'My AI'}</h1>
            <div className="view-toggle plan-switcher">
              {businessPlans.map((plan) => (
                <button
                  key={plan.id}
                  className={activeSelection?.type === 'plan' && activeSelection.id === plan.id ? 'active' : ''}
                  onClick={() => {
                    setActiveSelection({ type: 'plan', id: plan.id, title: plan.title });
                    setActiveView('glass');
                    setVisionMode(null);
                  }}
                >
                  {plan.title}
                </button>
              ))}
              {[...projects, ...sharedProjects].map((project) => (
                <button
                  key={project.id}
                  className={activeSelection?.type === 'project' && activeSelection.id === project.id ? 'active' : ''}
                  onClick={() => {
                    setActiveSelection({ type: 'project', id: project.id, title: project.title });
                    setActiveView('glass');
                    setVisionMode(null);
                  }}
                >
                  {project.title}
                </button>
              ))}
              <button className="new-plan-btn" onClick={() => setOnboarding({})}>+ New Plan</button>
            </div>
          </div>
          <div className="myai-header-center">
            <JourneyRibbon
              goal={activeGoal}
              plan={activePlan}
              hasEntryToday={hasEntryToday}
              onAim={() => setOnboarding({ existingGoal: activeGoal || undefined })}
              onPlan={() => goalNeedingPlan
                ? setOnboarding({ existingGoal: goalNeedingPlan, initialStep: 'manifestation' })
                : setOnboarding({})}
              onPractice={() => { setActiveView('glass'); setVisionMode(null); }}
              onMomentum={() => setVisionMode('board')}
            />
          </div>
          <div className="myai-header-right">
            <button
              className={activeView === 'scientific' ? 'active' : ''}
              onClick={() => setActiveView(activeView === 'scientific' ? 'glass' : 'scientific')}
            >
              {activeView === 'scientific' ? 'Journal' : 'Scientific Editor'}
            </button>
            {activeView === 'glass' && !onboarding && !visionMode && (
              <button onClick={() => setShowCalendar(!showCalendar)}>
                {showCalendar ? 'Hide Calendar' : 'Show Calendar'}
              </button>
            )}
            <button onClick={handleSignOut}>Sign Out</button>
          </div>
        </div>
      )}
      {renderMainContent()}
      </ErrorBoundary>
    </div>
  );
}

export default MyAIPage;
